import { Fragment, useContext, useEffect, useState, useSyncExternalStore, type ErrorInfo, type ReactNode } from "react"
import { LocalizationProvider } from "../localization/localization-provider"

import type { RemoteClient } from "../remote/remote-client"
import type { Presenter } from "../presentation/presenter"
import type { Session } from "../session/session"
import type { Storage } from "../storage/storage"
import type { Application } from "./application"
import { ApplicationContext, ApplicationScopeContext } from "./application-context"
import type { ApplicationPlugin } from "./composition"
import type { ApplicationScope } from "./plugin-runtime"
import { RenderErrorBoundary, type RenderErrorBoundaryFallbackContext } from "./render-error-boundary"

export interface ApplicationProviderProps {
  application: Application
  children: ReactNode
  renderErrorFallback?: (context: RenderErrorBoundaryFallbackContext) => ReactNode
  onRenderError?: (error: Error, info: ErrorInfo) => void
}

function ApplicationAccessBoundary({ application, children }: ApplicationProviderProps) {
  const authenticatedState = useSyncExternalStore(
    application.subscribeAuthenticatedState,
    () => application.authenticatedState,
    () => application.authenticatedState
  )

  if (authenticatedState.status === "loading") return null
  if (authenticatedState.status === "failed") throw authenticatedState.error

  return <Fragment key={authenticatedState.accessContextRevision}>{children}</Fragment>
}

export function ApplicationProvider({
  application,
  children,
  renderErrorFallback,
  onRenderError,
}: ApplicationProviderProps) {
  const fallback =
    renderErrorFallback ??
    (({ retry }) => (
      <div role="alert">
        <p>{application.localization.i18n.t(($) => $.errors.unexpected, { ns: "florisoft-framework" })}</p>
        <button type="button" onClick={retry}>
          {application.localization.i18n.t(($) => $.errors.retry, { ns: "florisoft-framework" })}
        </button>
      </div>
    ))
  const handleRenderError = (error: Error, info: ErrorInfo) => {
    application.telemetry.reportError(error, {
      "florisoft.error.source": "react-render",
      ...(info.componentStack == null ? {} : { "react.component_stack": info.componentStack }),
    })
    onRenderError?.(error, info)
  }
  const renderFallbackWithRecovery = (context: RenderErrorBoundaryFallbackContext) =>
    fallback({
      ...context,
      retry: () => {
        if (application.authenticatedState.status !== "failed") {
          context.retry()
          return
        }

        void application.retryAuthenticatedRoot().then(context.retry, context.retry)
      },
    })

  return (
    <RenderErrorBoundary fallback={renderFallbackWithRecovery} onError={handleRenderError}>
      <LocalizationProvider localization={application.localization}>
        <ApplicationContext.Provider value={application}>
          <ApplicationScopeContext.Provider value={application.rootScope}>
            <ApplicationAccessBoundary application={application}>{children}</ApplicationAccessBoundary>
          </ApplicationScopeContext.Provider>
        </ApplicationContext.Provider>
      </LocalizationProvider>
    </RenderErrorBoundary>
  )
}

export interface ApplicationPluginProviderProps {
  plugin: ApplicationPlugin | string
  children: ReactNode
  fallback?: ReactNode
}

export function ApplicationScopeProvider({ scope, children }: { scope: ApplicationScope; children: ReactNode }) {
  return <ApplicationScopeContext.Provider value={scope}>{children}</ApplicationScopeContext.Provider>
}

type PluginActivationState =
  | { readonly status: "activating"; readonly pluginId: string }
  | { readonly status: "active"; readonly pluginId: string; readonly scope: ApplicationScope }
  | { readonly status: "failed"; readonly pluginId: string; readonly error: unknown }

export function ApplicationPluginProvider({ plugin, children, fallback = null }: ApplicationPluginProviderProps) {
  const application = useApplication()
  const pluginId = typeof plugin === "string" ? plugin : plugin.id
  const [activation, setActivation] = useState<PluginActivationState>(() => ({
    status: "activating",
    pluginId,
  }))

  useEffect(() => {
    let active = true
    setActivation({ status: "activating", pluginId })

    void application.activatePlugin(pluginId).then(
      (scope) => {
        if (active) setActivation({ status: "active", pluginId, scope })
      },
      (error) => {
        if (active) setActivation({ status: "failed", pluginId, error })
      }
    )

    return () => {
      active = false
    }
  }, [application, pluginId])

  if (activation.pluginId !== pluginId) return fallback
  if (activation.status === "failed") throw activation.error
  if (activation.status !== "active") return fallback

  return <ApplicationScopeProvider scope={activation.scope}>{children}</ApplicationScopeProvider>
}

export function useApplication(): Application {
  const application = useContext(ApplicationContext)
  if (application == null) {
    throw new Error("useApplication must be used within ApplicationProvider.")
  }

  return application
}

export function useApplicationScope(): ApplicationScope {
  const scope = useContext(ApplicationScopeContext)
  if (scope == null) {
    throw new Error("useApplicationScope must be used within ApplicationProvider.")
  }
  return scope
}

export function useRemote(): RemoteClient {
  return useApplicationScope().remote
}

export function usePresenter(): Presenter {
  return useApplicationScope().presenter
}

export function useStorage(): Storage {
  return useApplication().storage
}

export function useTelemetry() {
  return useApplication().telemetry
}

export function useSession(): Session {
  const session = useApplication().session
  useSyncExternalStore(
    (listener) => session.subscribe(listener),
    () => session.snapshot,
    () => session.snapshot
  )
  return session
}
